import { Component, OnInit, ViewChild, inject } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { marker as _ } from '@biesbjerg/ngx-translate-extract-marker';
import { AuthorizationService } from '@nimmsta/ng-commons-lib/auth';
import { AppDialogService } from '@nimmsta/ng-commons-lib/services/app-dialog';
import {
  Column,
  CustomAction,
  DataTableComponent,
  EmptyTableComponent,
  NimmstaDataSource,
} from '@nimmsta/ng-commons-lib/data-table';
import { ErrorComponent } from '@nimmsta/ng-commons-lib/error';
import { HeaderBarComponent } from '@nimmsta/ng-commons-lib/header-bar';
import { NimmstaTimeAgoPipe } from '@nimmsta/ng-commons-lib/pipes';
import { TranslatePipe } from '@ngx-translate/core';
import { WorkflowDataStoreService } from '../services/workflowDataStore.service';
import { WorkflowDataStore } from './workflow-data-store';
import { WorkflowDataStoreFormComponent } from './workflow-data-store-form.component';
import { WorkflowDataStoreStateComponent } from './workflow-data-store-state.component';

_('workflow_data_store.title');
_('workflow_data_store.empty_text');

@Component({
  selector: 'app-workflow-data-store',
  templateUrl: './workflow-data-store.component.html',
  imports: [
    DataTableComponent,
    EmptyTableComponent,
    HeaderBarComponent,
    ErrorComponent,
    TranslatePipe,
  ],
  providers: [NimmstaTimeAgoPipe],
})
export class WorkflowDataStoreComponent implements OnInit {
  private authorizationService = inject(AuthorizationService);
  private service = inject(WorkflowDataStoreService);
  private modalService = inject(AppDialogService);
  private timeago = inject(NimmstaTimeAgoPipe);

  source!: NimmstaDataSource<WorkflowDataStore>;

  @ViewChild(DataTableComponent, { static: true })
  private tableComponent!: DataTableComponent<WorkflowDataStore>;

  customActions: CustomAction<WorkflowDataStore>[] = [];

  error?: HttpErrorResponse;

  canWriteDataConnections = false;

  get columns(): Column[] {
    return [
      new Column('name', 'workflow_data_store.name', { sortable: true }),
      new Column('identifier', 'workflow_data_store.identifier', {
        sortable: true,
      }),
      new Column('description', 'workflow_data_store.description'),
      new Column('lastUpdatedAt', 'lastUpdatedAt', {
        pipe: this.timeago,
        sortable: true,
        invertSorting: true,
      }),
    ];
  }

  ngOnInit(): void {
    this.canWriteDataConnections = this.authorizationService.hasPermission(
      'data_connection.write',
    );
    this.source = new NimmstaDataSource<WorkflowDataStore>(this.service);

    if (this.canWriteDataConnections) {
      const editAction = new CustomAction('edit');
      this.customActions.push(editAction);
      editAction.subscribe(this.edit.bind(this));

      const deleteAction = new CustomAction('delete', false, true);
      this.customActions.push(deleteAction);
      deleteAction.subscribe(this.delete.bind(this));
    }
  }

  new(): void {
    this.openModal({} as WorkflowDataStore);
  }

  show(id: string): void {
    const workflowDataStore = this.getSelectedObject(id);
    if (!workflowDataStore) {
      return;
    }
    this.modalService.open(WorkflowDataStoreStateComponent, {
      data: {
        workflowDataStore,
        canEdit: this.canWriteDataConnections,
      },
      size: 'xl',
    });
  }

  edit(id: string): void {
    const workflowDataStore = this.getSelectedObject(id);
    if (!workflowDataStore) {
      return;
    }
    this.openModal(workflowDataStore);
  }

  delete(id: string): void {
    this.service.delete(this.getSelectedObject(id)!).subscribe({
      next: () => this.tableComponent.loadDataPage(),
      error: (error: HttpErrorResponse) => {
        this.error = error;
      },
    });
  }

  private openModal(value: WorkflowDataStore): void {
    const ref = this.modalService.open(WorkflowDataStoreFormComponent, {
      data: { value },
    });
    const sub = ref.closed.subscribe(() => {
      this.tableComponent.loadDataPage();
      sub.unsubscribe();
    });
  }

  private getSelectedObject(id: string): WorkflowDataStore | undefined {
    return this.source.data?.content.filter((d) => id === d.id).pop();
  }
}
